/**
 * Code 39 encoder. Kept dependency-free so the same geometry renders on native,
 * on web, and inside the ViewShot capture used by the ID card download.
 */

export type BarcodeModule = {
  /** Width of this bar/space in narrow-bar units. */
  units: number;
  bar: boolean;
};

export type Barcode = {
  modules: BarcodeModule[];
  totalUnits: number;
  /** The characters actually encoded, after unsupported ones are dropped. */
  text: string;
};

const NARROW_UNITS = 1;
const WIDE_UNITS = 3;

// Each pattern is 9 elements, alternating bar/space and starting with a bar.
const PATTERNS: Record<string, string> = {
  '0': 'nnnwwnwnn',
  '1': 'wnnwnnnnw',
  '2': 'nnwwnnnnw',
  '3': 'wnwwnnnnn',
  '4': 'nnnwwnnnw',
  '5': 'wnnwwnnnn',
  '6': 'nnwwwnnnn',
  '7': 'nnnwnnwnw',
  '8': 'wnnwnnwnn',
  '9': 'nnwwnnwnn',
  A: 'wnnnnwnnw',
  B: 'nnwnnwnnw',
  C: 'wnwnnwnnn',
  D: 'nnnnwwnnw',
  E: 'wnnnwwnnn',
  F: 'nnwnwwnnn',
  G: 'nnnnnwwnw',
  H: 'wnnnnwwnn',
  I: 'nnwnnwwnn',
  J: 'nnnnwwwnn',
  K: 'wnnnnnnww',
  L: 'nnwnnnnww',
  M: 'wnwnnnnwn',
  N: 'nnnnwnnww',
  O: 'wnnnwnnwn',
  P: 'nnwnwnnwn',
  Q: 'nnnnnnwww',
  R: 'wnnnnnwwn',
  S: 'nnwnnnwwn',
  T: 'nnnnwnwwn',
  U: 'wwnnnnnnw',
  V: 'nwwnnnnnw',
  W: 'wwwnnnnnn',
  X: 'nwnnwnnnw',
  Y: 'wwnnwnnnn',
  Z: 'nwwnwnnnn',
  '-': 'nwnnnnwnw',
  '.': 'wwnnnnwnn',
  ' ': 'nwwnnnwnn',
  $: 'nwnwnwnnn',
  '/': 'nwnwnnnwn',
  '+': 'nwnnnwnwn',
  '%': 'nnnwnwnwn',
  '*': 'nwnnwnwnn',
};

const START_STOP = '*';

/** Uppercases and drops anything Code 39 cannot represent (including a literal `*`). */
export function sanitizeCode39(value: string): string {
  return (value || '')
    .toUpperCase()
    .split('')
    .filter(char => char !== START_STOP && PATTERNS[char] !== undefined)
    .join('');
}

/** Returns null when there is nothing encodable, so callers can skip rendering. */
export function encodeCode39(value: string): Barcode | null {
  const text = sanitizeCode39(value);
  if (!text) {
    return null;
  }

  const modules: BarcodeModule[] = [];
  let totalUnits = 0;

  const push = (units: number, bar: boolean) => {
    modules.push({ units, bar });
    totalUnits += units;
  };

  const chars = `${START_STOP}${text}${START_STOP}`.split('');
  chars.forEach((char, charIndex) => {
    PATTERNS[char].split('').forEach((element, elementIndex) => {
      push(element === 'w' ? WIDE_UNITS : NARROW_UNITS, elementIndex % 2 === 0);
    });
    // Narrow space between characters, but not after the stop character.
    if (charIndex < chars.length - 1) {
      push(NARROW_UNITS, false);
    }
  });

  return { modules, totalUnits, text };
}
